import React, { useRef } from "react";
import { MdAccountTree } from "react-icons/md";
import { BiTrash } from "react-icons/bi";
import { FiEdit2 } from "react-icons/fi";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Card, CardContent } from "@/components/ui/card";
import { useManageFundsController } from "./useManageFundsController";
import { cn } from "@/lib/utils";

export interface ManageFundsProps {
  onChange: () => void;
}

export const ManageFunds = (props: ManageFundsProps) => {
  const controller = useManageFundsController(props);

  const fundNameRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <Button
        variant="ghost"
        onClick={() => controller.setOpen(true)}
        className="w-full py-6 flex items-center justify-center"
      >
        <MdAccountTree className="mr-2" />
        Manage Funds
      </Button>

      <Dialog open={controller.open} onOpenChange={controller.setOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Manage Your Funds</DialogTitle>
            <DialogDescription>
              Create, edit, or remove your investment portfolios
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4 py-4">
            <div className="flex flex-col gap-3 max-h-[500px] overflow-y-auto">
              {(controller.funds || []).map((fund) => (
                <Card key={fund.fundId} className="overflow-hidden">
                  <CardContent className="flex items-center p-4">
                    <Avatar className="bg-primary/10 text-primary">
                      <AvatarFallback>
                        {fund.name[0].toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    {controller.editingFundId === fund.fundId ? (
                      <Input
                        defaultValue={fund.name}
                        autoFocus
                        onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                          controller.updateFund(fund, e.target.value)
                        }
                        onKeyDown={(
                          e: React.KeyboardEvent<HTMLInputElement>
                        ) => {
                          if (e.key === "Enter") {
                            controller.updateFund(fund, e.currentTarget.value);
                          }
                        }}
                        placeholder="Enter fund name"
                        className="flex-1 ml-4"
                      />
                    ) : (
                      <span className="flex-1 truncate ml-4">{fund.name}</span>
                    )}

                    <div className="flex gap-1">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() =>
                                controller.setEditingFundId(fund.fundId)
                              }
                              className="h-8 w-8"
                            >
                              <FiEdit2 className="h-4 w-4" />
                              <span className="sr-only">Edit</span>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Edit fund name</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              className={cn(
                                "h-8 w-8",
                                (controller.funds || []).length <= 1
                                  ? "opacity-50"
                                  : "text-destructive"
                              )}
                              disabled={(controller.funds || []).length <= 1}
                              onClick={() => {
                                controller.deleteFund(fund.fundId);
                              }}
                            >
                              <BiTrash className="h-4 w-4" />
                              <span className="sr-only">Delete</span>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {(controller.funds || []).length <= 1
                              ? "Cannot delete last fund"
                              : "Delete fund"}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Separator className="my-2" />

            <div className="space-y-2">
              <label htmlFor="fund-name" className="text-sm font-medium">
                Create New Fund
              </label>
              <Input
                id="fund-name"
                type="text"
                name="name"
                ref={fundNameRef}
                placeholder="Enter new fund name"
                required
              />
            </div>
            <DialogFooter className="px-0 pt-2">
              <Button
                type="submit"
                className="w-full"
                onClick={(e) => {
                  e.preventDefault();
                  controller.createNewFund(fundNameRef.current!.value.trim());
                  fundNameRef.current!.value = ""; // Clear the input after creating the fund
                }}
              >
                Create Fund
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
